import { TranslateService } from '@ngx-translate/core';
import { Injector } from '@angular/core';
import { DWSLanguagesService } from '../../services/dws/dws-languages.service';

// Cache for services to avoid repeated injector lookups
let cachedTranslateService: TranslateService | null = null;
let cachedDwsLanguagesService: DWSLanguagesService | null = null;
let lastInjector: Injector | null = null;

// Translation cache to avoid repeated lookups
const translationCache = new Map<string, string>();
let cacheInvalidated = false;

// Cache invalidation listener
let cacheInvalidationListener: (() => void) | null = null;

export function tr(message: string): string {
  const injector: Injector = (window as any)['injector'];
  if (!injector) {
    // console.debug('Injector not available for translation:', message);
    return message;
  }

  // Check if we need to invalidate cache (injector changed)
  if (lastInjector !== injector) {
    cachedTranslateService = null;
    cachedDwsLanguagesService = null;
    translationCache.clear();
    lastInjector = injector;
    cacheInvalidated = true;
  }

  try {
    // Get services with caching
    if (!cachedTranslateService) {
      cachedTranslateService = injector.get(TranslateService);
      
      // Set up cache invalidation listener
      if (!cacheInvalidationListener) {
        cacheInvalidationListener = () => {
          translationCache.clear();
          cacheInvalidated = true;
        };
        
        // Listen for language changes to invalidate cache
        cachedTranslateService.onLangChange.subscribe(cacheInvalidationListener);
        cachedTranslateService.onDefaultLangChange.subscribe(cacheInvalidationListener);
      }
    }
    
    if (!cachedDwsLanguagesService) {
      cachedDwsLanguagesService = injector.get(DWSLanguagesService);
    }

    const currentLang = cachedDwsLanguagesService.getCurrentLanguageIso();
    const cacheKey = `${currentLang}:${message}`;

    // Check cache first (keyed by language — avoids wrong locale after switch)
    if (!cacheInvalidated && translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey)!;
    }

    // Check if translations are loaded
    if (!cachedTranslateService.translations || Object.keys(cachedTranslateService.translations).length === 0) {
      // Don't log warning for every call, just return the message
      return message;
    }
    const fallbackLang = cachedDwsLanguagesService.getFallbackLanguageIso();

    let translation = cachedTranslateService.instant(message);

    if (translation === message && currentLang !== fallbackLang) {
      const fallbackTranslations = cachedTranslateService.translations[fallbackLang];
      if (fallbackTranslations && fallbackTranslations[message]) {
        translation = fallbackTranslations[message];
      }
    }

    // Cache the result
    translationCache.set(cacheKey, translation);
    cacheInvalidated = false;

    return translation;

  } catch (error) {
    //console.warn('Translation service error:', error, 'for message:', message);
    return message;
  }
}

/**
 * Replaces ngx-style {{key}} placeholders when translate.instant did not interpolate
 * (e.g. translations not loaded yet, or long keys with quotes). Uses a replacer function
 * so experience names containing `$` are safe.
 */
function applyInterpolationFallback(template: string, params: Record<string, unknown>): string {
  if (!template || !params) {
    return template;
  }
  let s = template;
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined || val === null) {
      continue;
    }
    const str = String(val);
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'g');
    s = s.replace(re, () => str);
  }
  return s;
}

/** ngx-translate `instant` with interpolation params (not cached; safe for dynamic values). */
export function trParams(message: string, params: Record<string, unknown>): string {
  const injector: Injector = (window as any)['injector'];
  if (!injector) {
    return applyInterpolationFallback(message, params);
  }
  try {
    const translate = injector.get(TranslateService);
    if (!translate.translations || Object.keys(translate.translations).length === 0) {
      return applyInterpolationFallback(message, params);
    }
    const translated = translate.instant(message, params);
    return applyInterpolationFallback(translated, params);
  } catch (error) {
    console.warn('Translation service error:', error, 'for message:', message);
    return applyInterpolationFallback(message, params);
  }
}

// Function to clear translation cache (useful for testing or manual cache invalidation)
export function clearTranslationCache(): void {
  translationCache.clear();
  cacheInvalidated = true;
}
